/**
 * Where a page goes, how big it is drawn, and how many real pixels that costs.
 *
 * **The arithmetic lives here** because none of it is testable through jsdom:
 * nothing lays out, so every rect is 0×0, and a `<canvas>` has no 2D context to
 * measure. `zoomMath.ts` is the same file for the image viewer and states the
 * same reason; the two are deliberately separate because their models are not
 * the same one parameterised.
 *
 * **What is different from `zoomMath`, and why FIL-8b insisted on it.** The
 * image viewer scales a decoded bitmap with a CSS transform and leans on the
 * compositor to re-rasterize once a gesture settles — correct for a photograph,
 * where zooming reveals no detail the file does not already hold. A document
 * zoomed is text that has to stay legible, so the page is **rendered again** at
 * the new scale rather than magnified, and the numbers below are the ones that
 * decide the size of that render. `MIN_SCALE` is also below 1 here, where the
 * image viewer's floor is exactly fit: a reader zooms a page out to see its
 * shape, which is what the browser's own viewer allows.
 *
 * **Since ADR-0101 that difference is one of duration rather than of kind.** A
 * pinch here magnifies too, for as long as the fingers are down — because a
 * renderer cannot keep up with a gesture, and re-rendering per touch sample
 * produced a blank page rather than a sharp one. What still separates the two
 * viewers is what happens when the gesture stops: a photograph is handed back
 * to the compositor to re-rasterize, and a document is **drawn again** at the
 * scale below. The gesture's own arithmetic is `pinchZoom.ts`, deliberately not
 * this file — these numbers are consulted on every render, those only while two
 * fingers are down.
 */

export interface Size {
  width: number;
  height: number;
}

/** A quarter of the page's own size, and five times it. The browser's own band. */
export const MIN_SCALE = 0.25;
export const MAX_SCALE = 5;

/**
 * The rungs `+` and `−` move between.
 *
 * Chrome's own ladder, close enough that a reader who knows one knows the
 * other: dense around 100% where the difference between 90 and 110 is the
 * difference between reading and not, and coarse at the ends where it is not.
 */
export const ZOOM_LADDER: readonly number[] = [
  0.25, 0.33, 0.5, 0.67, 0.75, 0.8, 0.9, 1, 1.1, 1.25, 1.5, 1.75, 2, 2.5, 3, 4, 5,
];

/**
 * Never draw more than two canvas pixels per CSS pixel.
 *
 * A reported ratio of 3 or 4 exists — recent phones, and any display with the
 * browser zoomed — and honouring it quadruples the memory for a sharpness
 * difference nobody has ever reported seeing.
 */
const MAX_DEVICE_PIXEL_RATIO = 2;

/** A stage smaller than this has not been laid out yet, whatever it claims. */
const MEASURABLE_PX = 24;

export type FitMode = 'page' | 'width';

/**
 * The scale at which a page fits the stage.
 *
 * `page` fits the whole thing, on whichever axis binds; `width` fills the width
 * and lets the page run off the bottom, which is what a reader who is reading
 * rather than surveying actually wants.
 *
 * **A stage of nothing answers 1, not 0.** jsdom never lays out, and a real
 * browser's first frame runs before it has — and a scale of zero means a canvas
 * of no pixels, which is a blank viewer that never recovers because nothing
 * re-measures a page it decided was empty.
 */
export function fitScale(stage: Size, page: Size, mode: FitMode, gutter: number): number {
  if (stage.width < MEASURABLE_PX || stage.height < MEASURABLE_PX) return 1;
  if (page.width <= 0 || page.height <= 0) return 1;

  const available = {
    width: Math.max(1, stage.width - gutter * 2),
    height: Math.max(1, stage.height - gutter * 2),
  };

  const byWidth = available.width / page.width;
  const scale = mode === 'width' ? byWidth : Math.min(byWidth, available.height / page.height);

  return clampPdfScale(scale);
}

export function clampPdfScale(scale: number): number {
  return Math.min(MAX_SCALE, Math.max(MIN_SCALE, scale));
}

/**
 * Fold any number of turns into the four the format allows.
 *
 * A page carries its own `/Rotate` and the reader turns it again, so the two
 * compose and run past 360 within four presses — and `/Rotate` is signed, so
 * `-90` is a value real documents contain. Written as a plain `% 360` this
 * returns a negative number and every viewport built on it is wrong.
 *
 * The rounding is not defensive padding: producers emit 89 and 91, pdf.js
 * rounds them itself, and a viewport built on 91 degrees shears the page.
 */
export function normalizeRotation(degrees: number): 0 | 90 | 180 | 270 {
  const quarters = ((Math.round(degrees / 90) % 4) + 4) % 4;

  return (quarters * 90) as 0 | 90 | 180 | 270;
}

/** A quarter turn swaps the axes; a half turn leaves them. */
export function rotatedSize(size: Size, degrees: number): Size {
  return normalizeRotation(degrees) % 180 === 0
    ? { width: size.width, height: size.height }
    : { width: size.height, height: size.width };
}

/**
 * The next rung up or down.
 *
 * `direction` is `1` or `-1`. A scale that is not on the ladder — which is
 * every scale `fitScale` produces — joins it at the nearest rung in the
 * direction asked for, rather than snapping to whatever the last press left.
 */
export function nextZoomStep(scale: number, direction: 1 | -1): number {
  // A hair of tolerance so that a rung reached by pressing `+` is recognised as
  // that rung and not as a number just below it.
  const epsilon = 0.0001;

  if (direction === 1) {
    return ZOOM_LADDER.find((rung) => rung > scale + epsilon) ?? MAX_SCALE;
  }

  return [...ZOOM_LADDER].reverse().find((rung) => rung < scale - epsilon) ?? MIN_SCALE;
}

/**
 * How many canvas pixels per CSS pixel this page can afford.
 *
 * **The assertion this exists for is that a tab stays alive.** A letter page at
 * 500% is 3060×3960 CSS pixels; at a device ratio of 2 that is 24 megapixels
 * and about 97 MB for one canvas, and a discovery packet has hundreds of pages.
 * iOS Safari does not throw when a canvas is too large — it returns a blank one
 * with no error, which is indistinguishable from a broken viewer.
 *
 * Backing off the density is the right lever because it is the only one that
 * does not change what the reader asked for: the page is still at 500%, drawn
 * slightly softer, rather than silently at 300%.
 */
export function deviceScale(
  cssWidth: number,
  cssHeight: number,
  devicePixelRatio: number,
  maxPixels: number,
): number {
  const capped = Math.min(Math.max(devicePixelRatio, 1), MAX_DEVICE_PIXEL_RATIO);
  const pixels = cssWidth * capped * (cssHeight * capped);

  if (pixels <= maxPixels) return capped;

  // Area scales with the square of the ratio, so the room left is its root.
  return Math.max(0.1, capped * Math.sqrt(maxPixels / pixels));
}
